import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { waLink, formatDateLong } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { WhatsappLogo, Warning } from "@phosphor-icons/react";

const REASONS = ["Permintaan pasien", "Petugas berhalangan", "Perubahan jadwal klinik", "Kondisi darurat", "Lainnya"];
const STATUS_OPTS = ["Menunggu Konfirmasi", "Dikonfirmasi", "Reschedule", "Sudah Datang", "Selesai", "Batal", "No Show"];
const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 disabled:bg-slate-50 disabled:text-slate-400";
const labelCls = "mb-1 block text-xs font-semibold text-slate-500";

export default function RescheduleDialog({ reservation: r, open, onClose, onDone }) {
  const { user } = useAuth();
  const role = user?.role;
  const isPetugas = role === "petugas";
  const isSuper = role === "super_admin";

  const [f, setF] = useState({});
  const [reason, setReason] = useState("");
  const [reasonNote, setReasonNote] = useState("");
  const [override, setOverride] = useState(false);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [saved, setSaved] = useState(null); // holds {old, new} after success

  useEffect(() => {
    if (r) {
      setF({ tanggal_kunjungan: r.tanggal_kunjungan || "", jam_kunjungan: r.jam_kunjungan || "", petugas_nama: r.petugas_nama || "", jenis_layanan: r.jenis_layanan || "", status: r.status || "" });
      setReason(""); setReasonNote(""); setOverride(false); setConfirm(false); setSaved(null);
    }
  }, [r]);

  useEffect(() => {
    if (open) {
      api.get("/staff").then((res) => setStaff(res.data)).catch(() => setStaff([]));
      api.get("/services").then((res) => setServices(res.data)).catch(() => setServices([]));
    }
  }, [open]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const doSave = async () => {
    setSaving(true);
    try {
      const alasan = reason === "Lainnya" ? (reasonNote || "Lainnya") : reason;
      const payload = { tanggal_kunjungan: f.tanggal_kunjungan, jam_kunjungan: f.jam_kunjungan, alasan_perubahan: alasan, override };
      if (!isPetugas) { payload.petugas_nama = f.petugas_nama; payload.jenis_layanan = f.jenis_layanan; payload.status = f.status; }
      const { data } = await api.patch(`/reservations/${r.id}/edit`, payload);
      toast.success("Perubahan tersimpan");
      setConfirm(false);
      setSaved({
        old: { tanggal: r.tanggal_kunjungan, jam: r.jam_kunjungan },
        cur: data,
      });
    } catch (e) { setConfirm(false); toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const waMessage = () => {
    const c = saved.cur; const o = saved.old;
    return (
      `Halo ${c.nama_lengkap} 🤍\n` +
      `Kami ingin menginformasikan bahwa terdapat perubahan jadwal reservasi Anda di GBS Clinic & Birth Center.\n` +
      `Layanan: ${c.jenis_layanan}\n` +
      `Tanggal: ${formatDateLong(c.tanggal_kunjungan)}\n` +
      `Jam: ${c.jam_kunjungan}\n` +
      `Petugas: ${c.petugas_nama || "-"}\n` +
      `Lokasi: ${c.lokasi || "-"}\n` +
      `Jadwal sebelumnya: ${formatDateLong(o.tanggal)} pukul ${o.jam}.\n` +
      `Mohon konfirmasi apabila jadwal baru tersebut sesuai.\n` +
      `Terima kasih 🤍\nGBS Clinic & Birth Center`
    );
  };

  if (!r) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-md" data-testid="reschedule-dialog">
        <DialogHeader><DialogTitle className="font-heading">Edit / Reschedule Reservasi</DialogTitle></DialogHeader>

        {saved ? (
          <div className="space-y-4" data-testid="reschedule-success">
            <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">Jadwal <b>{saved.cur.nama_lengkap}</b> berhasil diperbarui ke <b>{formatDateLong(saved.cur.tanggal_kunjungan)} {saved.cur.jam_kunjungan}</b>.</div>
            <a data-testid="wa-send-change" href={waLink(saved.cur.no_whatsapp, waMessage())} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] py-3 text-sm font-bold text-white active:scale-95">
              <WhatsappLogo size={20} weight="fill" /> Kirim Info Perubahan via WhatsApp
            </a>
            <button onClick={onDone} className="w-full rounded-xl bg-slate-100 py-2.5 text-sm font-semibold text-slate-600">Selesai</button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <p className="rounded-lg bg-[#F7F0FF] px-3 py-2 text-xs text-slate-500">Jadwal saat ini: <b>{formatDateLong(r.tanggal_kunjungan)} · {r.jam_kunjungan}</b> · {r.petugas_nama || "belum ada petugas"}</p>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Tanggal Baru</label><input type="date" data-testid="edit-date" value={f.tanggal_kunjungan} onChange={(e) => set("tanggal_kunjungan", e.target.value)} className={inputCls} /></div>
                <div><label className={labelCls}>Jam Baru</label><input type="time" data-testid="edit-time" value={f.jam_kunjungan} onChange={(e) => set("jam_kunjungan", e.target.value)} className={inputCls} /></div>
              </div>
              <div>
                <label className={labelCls}>Petugas {isPetugas && <span className="text-slate-400">(tidak dapat diubah)</span>}</label>
                <select data-testid="edit-petugas" disabled={isPetugas} value={f.petugas_nama} onChange={(e) => set("petugas_nama", e.target.value)} className={inputCls}>
                  <option value="">-- Pilih Petugas --</option>
                  {staff.map((s) => <option key={s.id} value={s.nama}>{s.nama} ({s.jenis})</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Layanan {isPetugas && <span className="text-slate-400">(tidak dapat diubah)</span>}</label>
                <select data-testid="edit-layanan" disabled={isPetugas} value={f.jenis_layanan} onChange={(e) => set("jenis_layanan", e.target.value)} className={inputCls}>
                  {!services.some((s) => s.nama === f.jenis_layanan) && f.jenis_layanan && <option value={f.jenis_layanan}>{f.jenis_layanan}</option>}
                  {services.map((s) => <option key={s.id} value={s.nama}>{s.nama}</option>)}
                </select>
              </div>
              {!isPetugas && (
                <div>
                  <label className={labelCls}>Status</label>
                  <select data-testid="edit-status" value={f.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
                    {STATUS_OPTS.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className={labelCls}>Alasan Perubahan (opsional)</label>
                <select data-testid="edit-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls}>
                  <option value="">-- Pilih alasan --</option>
                  {REASONS.map((x) => <option key={x}>{x}</option>)}
                </select>
                {reason === "Lainnya" && <input data-testid="edit-reason-note" value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} placeholder="Tulis alasan..." className={inputCls + " mt-2"} />}
              </div>
              {isSuper && (
                <label className="flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-700">
                  <input data-testid="edit-override" type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
                  <span className="flex items-center gap-1"><Warning size={14} weight="fill" /> Override (izinkan double booking / di luar jam)</span>
                </label>
              )}
            </div>
            <DialogFooter>
              <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
              <button data-testid="edit-save" onClick={() => setConfirm(true)} className="rounded-xl bg-[#7C4DE0] px-5 py-2 text-sm font-bold text-white hover:bg-[#6A38C7]">Simpan Perubahan</button>
            </DialogFooter>
          </>
        )}

        {/* Confirmation modal (patient exists) */}
        <Dialog open={confirm} onOpenChange={(v) => !v && setConfirm(false)}>
          <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="confirm-change-dialog">
            <DialogHeader><DialogTitle className="font-heading">Ubah Jadwal Pasien?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">Reservasi ini sudah memiliki pasien. Perubahan tanggal, jam, layanan, atau petugas akan mengubah jadwal kunjungan pasien. Lanjutkan?</p>
            <DialogFooter>
              <button data-testid="confirm-cancel" onClick={() => setConfirm(false)} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
              <button data-testid="confirm-save-yes" onClick={doSave} disabled={saving} className="rounded-xl bg-[#7C4DE0] px-5 py-2 text-sm font-bold text-white hover:bg-[#6A38C7] disabled:opacity-60">{saving ? "Menyimpan..." : "Ya, Simpan Perubahan"}</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
